// areaPipelineTimeline — Alpine factory for ONE area enrichment pipeline
// panel on the wizard's Generate step (Task 16).
//
// Multiple areas can each have a tracked area_pipeline_runs chain in flight
// at once, so the Generate step renders one panel PER scoped area (see
// templates/components/wizard/step_generate.templ's areaPipelinePanel) —
// and each panel gets its OWN instance of this factory, keyed by areaID.
// This is deliberate, not incidental: area-pipeline-progress fans out on
// `window` to every panel on the page (mirrors website_build_progress /
// wizardGenerationProgress's own window-event pattern — see
// templates/layout.templ's htmx:sseMessage bridge), so if N panels shared
// ONE x-data scope, panel 0's row would read panel 1's status the moment
// Alpine resolved `stAt`/`stages` up the shared scope chain. Giving every
// panel its own component instance means its own `stages` array and its own
// stAt/stProgress/stRepeat methods (buildTimelineGetters, shared with the
// website-build timeline) — the areaID filter below is what keeps a panel
// from applying another area's snapshot even though every panel's listener
// fires on every event.
import { buildTimelineGetters, type BuildStage } from "./build-progress-timeline";

export function areaPipelineTimeline({ areaID, runID, stages, status, phase }: any) {
  return {
    areaID: areaID as string,
    runID: (runID || "") as string,
    stages: (stages || []) as BuildStage[],
    status: status as string,
    phase: (phase || "") as string,

    onProgress(detail: any) {
      let d: any;
      try {
        d = JSON.parse(detail.data);
      } catch {
        return;
      }
      // area-pipeline-progress fans out to every area panel on the page (and
      // every browser tab of the acting operator); ignore any area other
      // than this panel's own.
      if (d.area_id !== this.areaID) return;
      this.stages = d.stages || this.stages;
      this.status = d.status;
      this.phase = d.phase || this.phase;
      if (d.run_id) this.runID = d.run_id;
    },

    isTerminal(): boolean {
      return this.status === "completed" || this.status === "failed";
    },

    // ── Presentation helpers (server-rendered rows bind to these) ──
    // stAt / stProgress / stRepeat are shared with the website-build
    // timeline — see build-progress-timeline.ts. grpDone/grpStatus/
    // grpActiveLabel/unitLabel come along too (unused here: the area
    // timeline has no grouping concept) rather than duplicating the spread.
    ...buildTimelineGetters(),
  };
}

// areaPipelineSection — Alpine factory for the ONE wrapper around all of the
// panels above (step_generate.templ's areaPipelineProgressSection).
//
// It exists to answer a question no single panel can: "are they ALL done?".
// The panels are deliberately isolated from one another (see this file's
// header) so none of them can see its siblings' status — which is correct
// for rendering a timeline, and useless for deciding whether the section as
// a whole should still be shouting "werden angereichert...".
//
// The aggregate lives here instead, as an areaID -> status map seeded
// server-side with every tracked run. Each panel writes its OWN id into it
// from its own event expression:
//
//     @area-pipeline-progress.window="onProgress($event.detail); noteStatus(areaID, status)"
//
// `noteStatus` is not on the panel's scope, so Alpine resolves it up the
// scope chain to this component; `areaID`/`status` resolve to the panel's
// own. That is a keyed write UP into a parent, not the accidental sideways
// read the panel isolation guards against — panel 0 still cannot see panel
// 1's stages, only this map's own-id slot ever changes per panel.
export function areaPipelineSection({ statuses }: any) {
  return {
    statuses: (statuses || {}) as Record<string, string>,
    // Disclosure state for the collapsed "N Regionen angereichert" receipt.
    // Only reachable when collapsible() is true.
    expanded: false,

    noteStatus(areaID: string, status: string) {
      if (!areaID || !status) return;
      this.statuses[areaID] = status;
    },

    allTerminal(): boolean {
      const v = Object.values(this.statuses);
      // An EMPTY map is not "all terminal" — vacuous truth would collapse a
      // section whose statuses never seeded, hiding live panels behind a
      // receipt claiming work finished that may not have started.
      return v.length > 0 && v.every((s) => s === "completed" || s === "failed");
    },

    anyFailed(): boolean {
      return Object.values(this.statuses).some((s) => s === "failed");
    },

    // settledWithFailure: everything stopped and at least one area did not
    // make it. Deliberately NOT just anyFailed() — while other areas are
    // still enriching, "some areas could not be enriched" would be a verdict
    // on work still in progress. Until then the live heading stands and the
    // failed panel's own error line carries the news.
    settledWithFailure(): boolean {
      return this.allTerminal() && this.anyFailed();
    },

    // collapsible: every run reached a terminal state and none of them
    // failed, so there is a clean receipt to show INSTEAD of N settled
    // timelines. A failure keeps the section open at full detail — it is the
    // only place the operator learns an area guide will be thin.
    collapsible(): boolean {
      return this.allTerminal() && !this.anyFailed();
    },

    showPanels(): boolean {
      return !this.collapsible() || this.expanded;
    },
  };
}
